import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  ApolloLink,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';

// Debug flag - set to true to enable verbose logging
const DEBUG_API = true;

// In development, use relative URLs to go through Vite proxy
// In production, use the configured API URL
const getGraphQLUri = (): string => {
  if (import.meta.env.DEV) {
    return '/api/graphql';
  }
  return import.meta.env.VITE_VTWA_GRAPHQL_URL || 'https://api.varsitytutors.com/graphql';
};

const graphqlUri = getGraphQLUri();

if (DEBUG_API) {
  console.log('[GraphQL Client] Initializing with:', {
    uri: graphqlUri,
    env: {
      DEV: import.meta.env.DEV,
      VITE_VTWA_GRAPHQL_URL: import.meta.env.VITE_VTWA_GRAPHQL_URL,
    },
  });
}

const httpLink = createHttpLink({
  uri: graphqlUri,
  // Send cookies with cross-origin requests (required for subdomain auth)
  credentials: 'include',
});

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (DEBUG_API) {
    console.log('[GraphQL] Operation:', {
      operationName: operation.operationName,
      variables: operation.variables,
    });
  }

  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      );
    });
    
    if (DEBUG_API) {
      console.error('[GraphQL] Schema mismatch likely - check if these types/queries exist in backend schema');
    }
  }
  
  if (networkError) {
    console.error(`[Network error]: ${networkError}`);
    
    if (DEBUG_API) {
      console.error('[GraphQL] Network error details:', {
        name: networkError.name,
        message: networkError.message,
        // @ts-expect-error - statusCode may exist on network errors
        statusCode: networkError.statusCode,
      });
    }
    
    // Handle authentication errors
    if ('statusCode' in networkError && networkError.statusCode === 401) {
      window.location.href = '/login';
    }
  }
});

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        // Configure cache policies for specific queries
        clients: {
          keyArgs: ['filter', 'sort'],
          merge(existing, incoming, { args }) {
            if (!args?.offset || args.offset === 0) {
              return incoming;
            }
            return {
              ...incoming,
              nodes: [...(existing?.nodes || []), ...incoming.nodes],
            };
          },
        },
        leads: {
          keyArgs: ['filter', 'sort'],
          merge(existing, incoming, { args }) {
            if (!args?.offset || args.offset === 0) {
              return incoming;
            }
            return {
              ...incoming,
              nodes: [...(existing?.nodes || []), ...incoming.nodes],
            };
          },
        },
        tutors: {
          keyArgs: ['filter', 'sort'],
          merge(existing, incoming, { args }) {
            if (!args?.offset || args.offset === 0) {
              return incoming;
            }
            return {
              ...incoming,
              nodes: [...(existing?.nodes || []), ...incoming.nodes],
            };
          },
        },
      },
    },
    Client: {
      keyFields: ['id'],
    },
    Lead: {
      keyFields: ['id'],
    },
    Tutor: {
      keyFields: ['uuid'],
    },
    User: {
      keyFields: ['id'],
    },
    Student: {
      keyFields: ['id'],
    },
    Assignment: {
      keyFields: ['id'],
    },
    Todo: {
      keyFields: ['id'],
    },
  },
});

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, httpLink]),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'cache-first',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});
